// ─── Commercial ───────────────────────────────────────────────────────────────

import type { DocLabel, QAQuestion } from "./preconConfig.types";

export const commercialDocLabels: DocLabel[] = [
  {
    key: "project_description",
    label: "Description of the Project",
    isCommercial: true,
    optional: false,
  },
  {
    key: "electronic_plan",
    label: "Electronic Plans",
    extra:
      "(Architectural, Structural, Civil, Mechanical, Electrical if available)",
    optional: true,
  },
  { key: "specification", label: "Specifications", optional: false },
  {
    key: "signed_contract",
    label: "Signed Contract",
    extra: "(with all attachments)",
    optional: true,
  },
  { key: "project_schedule", label: "Project Schedule", optional: false },
  {
    key: "required_permits",
    label: "Copy of all Required Permits",
    optional: false,
  },
  {
    key: "insurance_certificate",
    label: "Certificates for G/L Insurance & Builders Risk Insurance",
    optional: false,
  },
  { key: "bonds", label: "Bonds", extra: "(if required)", optional: true },
  {
    key: "architect_rendering_elevation",
    label: "Architect's Rendering / Elevation",
    extra: "(PDF or JPEG)",
    optional: true,
  },
  {
    key: "budget_breakdown",
    label: "Broken-out Budget / Schedule of Values",
    optional: false,
  },
  {
    key: "geotechnical_report",
    label: "Geotechnical Report",
    isCommercial: true,
    optional: false,
  },
  {
    key: "available_utility",
    label: "Verification of Utility Availability",
    isCommercial: true,
    extra:
      "Statement from engineer confirming utility availability to support the building's intended use",
    optional: false,
  },
];

export const commercialDocFileNames: string[] = [
  "project_description.pdf",
  "plans_harborview.pdf",
  "specs_harborview.pdf",
  "contract_signed.pdf",
  "schedule_harborview.pdf",
  "permits_harborview.pdf",
  "insurance_certs.pdf",
  "bonds.pdf",
  "elevation_rendering.jpg",
  "budget_sov.xlsx",
  "geotechnical_report.pdf",
  "utility_verification.pdf",
];

export const commercialQAQuestions: QAQuestion[] = [
  {
    key: "project_primary_description",
    label: "Provide a primary description of the project.",
    placeholder: "Describe the project scope, purpose, and key details…",
    type: "textarea",
  },
  {
    key: "total_cost",
    label: "What is the total project cost?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 1500000",
    prefix: "$",
  },
  {
    key: "hard_cost",
    label: "What are the project's hard costs?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 1200000",
    prefix: "$",
  },
  {
    key: "soft_cost",
    label: "What are the project's soft costs?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 300000",
    prefix: "$",
  },
  {
    key: "monthly_meeting_plan",
    label:
      "Are project monthly meetings planned? If so, please provide the day and time.",
    placeholder:
      "Yes / No — if yes, provide recurring day and time (e.g. First Tuesday of each month, 10:00 AM)",
    type: "textarea",
  },
  {
    key: "square_footage",
    label:
      "What is the approximate total square footage of the entire project?",
    hint: "If multiple buildings, please state the square footage of each building separately.",
    placeholder: "e.g. 20400",
    type: "input",
    numeric: true,
    maxLength: 50,
  },
  {
    key: "build_permit_payer",
    label: "Who is paying for the building permit?",
    placeholder: "Owner / GC / Other — explain",
    type: "input",
  },
  {
    key: "builder_risk_insurance_provider",
    label: "Who is providing Builders Risk insurance — Owner or GC?",
    placeholder: "Owner / GC",
    type: "input",
  },
  {
    key: "is_bond_required",
    label: "Are bonds required?",
    placeholder: "Yes / No — if yes, describe type and amount",
    type: "input",
  },
  {
    key: "shared_saving",
    label:
      "Is there Shared Savings (if a GMP)? If yes, please explain the shared savings arrangement.",
    placeholder: "Yes / No — if yes, explain the Shared Savings arrangement",
    type: "textarea",
  },
  {
    key: "damages_or_early_completion_bonus",
    label:
      "Are there liquidated damages or early completion bonuses? If yes, please explain.",
    placeholder: "Yes / No — if yes, explain terms and amounts",
    type: "textarea",
  },
  {
    key: "contractor_fees",
    label: "What is the contractor's fee for the project?",
    placeholder: "e.g. 50000",
    type: "input",
    numeric: true,
    maxLength: 15,
    prefix: "$",
  },
  {
    key: "contingency_amount",
    label: "What is the contingency amount?",
    placeholder: "$ amount or N/A",
    type: "subfields",
    subfields: [
      {
        key: "currencyCode",
        label: "Currency",
        placeholder: "USD",
        defaultValue: "USD",
      },
      {
        key: "amount",
        label: "Amount",
        placeholder: "e.g. 50000",
        numeric: true,
        maxLength: 15,
        prefix: "$",
      },
    ],
  },
  {
    key: "preconstruction_fees",
    optional: true,
    label: "Is there a preconstruction fee? If yes, please list the amount.",
    placeholder: "Yes / No — if yes, $ amount",
    type: "subfields",
    subfields: [
      {
        key: "currencyCode",
        label: "Currency",
        placeholder: "USD",
        defaultValue: "USD",
      },
      {
        key: "amount",
        label: "Amount",
        placeholder: "e.g. 10000",
        numeric: true,
        maxLength: 15,
        prefix: "$",
      },
    ],
  },

  {
    key: "mobilization_date",
    label: "What is the project mobilization date?",
    type: "date",
  },
  {
    key: "estimated_date",
    label: "What is the estimated project completion date?",
    type: "date",
  },
];

// ─── Residential ──────────────────────────────────────────────────────────────

export const residentialDocLabels: DocLabel[] = [
  {
    key: "electronic_plan",
    label: "Electronic Plans",
    extra:
      "(Architectural, Structural, Civil, Mechanical, Electrical if available)",
    optional: true,
  },
  { key: "specification", label: "Specifications", optional: false },
  {
    key: "signed_contract",
    label: "Signed Contract",
    extra: "(with all attachments)",
    optional: true,
  },
  { key: "project_schedule", label: "Project Schedule", optional: false },
  {
    key: "required_permits",
    label: "Copy of all Required Permits",
    optional: false,
  },
  {
    key: "insurance_certificate",
    label: "Certificates for G/L Insurance & Builders Risk Insurance",
    optional: false,
  },
  { key: "bonds", label: "Bonds", extra: "(if required)", optional: true },
  {
    key: "architect_rendering_elevation",
    label: "Architect's Rendering / Elevation",
    extra: "(PDF or JPEG)",
    optional: true,
  },
  {
    key: "budget_breakdown",
    label: "Broken-out Budget / Schedule of Values",
    optional: false,
  },
];

export const residentialDocFileNames: string[] = [
  "plans_14elm.pdf",
  "specs_14elm.pdf",
  "contract_signed.pdf",
  "schedule.pdf",
  "permits.pdf",
  "insurance_certs.pdf",
  "bonds.pdf",
  "elevation_rendering.jpg",
  "budget_sov.xlsx",
];

export const residentialQAQuestions: QAQuestion[] = [
  {
    key: "square_footage",
    label: "What is the square footage of the following areas?",
    type: "subfields",
    subfields: [
      {
        key: "total_sqft",
        label: "Total (sq ft)",
        placeholder: "e.g. 2400",
        numeric: true,
        maxLength: 12,
      },
      {
        key: "house",
        label: "House (sq ft)",
        placeholder: "e.g. 2400",
        numeric: true,
        maxLength: 12,
      },
      {
        key: "garage",
        label: "Garage (sq ft)",
        placeholder: "e.g. 576",
        numeric: true,
        maxLength: 12,
      },
      {
        key: "unfinished",
        label: "Unfinished above ground",
        placeholder: "sq ft or N/A",
      },
      {
        key: "basement",
        label: "Finished basement",
        placeholder: "sq ft or N/A",
      },
      {
        key: "detached",
        label: "Detached buildings in scope",
        placeholder: "Description and sq ft, or N/A",
        fullWidth: true,
      },
    ],
  },
  {
    key: "total_cost",
    label: "What is the total project cost?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 1500000",
    prefix: "$",
  },
  {
    key: "hard_cost",
    label: "What are the project's hard costs?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 1200000",
    prefix: "$",
  },
  {
    key: "soft_cost",
    label: "What are the project's soft costs?",
    type: "input",
    numeric: true,
    maxLength: 20,
    placeholder: "e.g. 300000",
    prefix: "$",
  },
  {
    key: "project_primary_description",
    label: "Provide a primary description of the project.",
    placeholder: "Describe the project scope, purpose, and key details…",
    type: "textarea",
  },
  {
    key: "build_permit_payer",
    label: "Who is paying for the building permit?",
    placeholder: "Owner / GC / Other — explain",
    type: "input",
  },
  {
    key: "builder_risk_insurance_provider",
    label: "Who is providing Builders Risk insurance — Owner or GC?",
    placeholder: "Owner / GC",
    type: "input",
  },
  {
    key: "is_bond_required",
    label: "Are bonds required?",
    placeholder: "Yes / No — if yes, describe",
    type: "input",
  },
  {
    key: "shared_saving",
    label: "Is there Shared Savings (if a GMP)? If yes, please explain.",
    placeholder: "Yes / No — if yes, explain the Shared Savings arrangement",
    type: "textarea",
  },
  {
    key: "damages_or_early_completion_bonus",
    label:
      "Are there liquidated damages or early completion bonuses? If yes, please explain.",
    placeholder: "Yes / No — if yes, explain terms",
    type: "textarea",
  },
  {
    key: "contractor_fees",
    label: "What is the contractor fee?",
    placeholder: "e.g. 50000",
    type: "input",
    numeric: true,
    maxLength: 15,
    prefix: "$",
  },
  {
    key: "contingency_amount",
    label: "What is the contingency amount?",
    placeholder: "$ amount or N/A",
    type: "subfields",
    subfields: [
      {
        key: "currencyCode",
        label: "Currency",
        placeholder: "USD",
        defaultValue: "USD",
      },
      {
        key: "amount",
        label: "Amount",
        placeholder: "e.g. 50000",
        numeric: true,
        maxLength: 15,
        prefix: "$",
      },
    ],
  },

  {
    key: "mobilization_date",
    label: "What is the project mobilization date?",
    type: "date",
  },
  {
    key: "estimated_date",
    label: "What is the estimated project completion date?",
    type: "date",
  },
  {
    key: "any_scope_from_owner_borrower",
    label:
      "Is there any scope being completed by the Owner / Borrower? If so, please list.",
    placeholder: "Yes / No — if yes, list all owner-completed scope items",
    type: "textarea",
  },
];

// ─── Selector ─────────────────────────────────────────────────────────────────

export const getPreconConfig = (
  type: string,
): {
  docLabels: DocLabel[];
  docFileNames: string[];
  qaQuestions: QAQuestion[];
} => {
  const isResidential = type === "residential";
  return {
    docLabels: isResidential ? residentialDocLabels : commercialDocLabels,
    docFileNames: isResidential
      ? residentialDocFileNames
      : commercialDocFileNames,
    qaQuestions: isResidential ? residentialQAQuestions : commercialQAQuestions,
  };
};
